/**
 * Inline code toggling (Ctrl/Cmd+E, Notion-style): wrap the current text
 * selection in <code>, or unwrap it if it's already code.
 *
 * Works inside a single editable block (contenteditable can't select across
 * blocks anyway). After changing the DOM an `input` event is dispatched so
 * react-contenteditable's onChange commits the new HTML to the block tree.
 * Selection is restored by character offset after that re-render, so a
 * toolbar click keeps the highlight.
 */

const closestCode = (node) => {
  let el = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  while (el) {
    if (el.tagName === 'CODE') return el;
    if (el.classList?.contains('doc-editable')) return null; // stop at block edge
    el = el.parentElement;
  }
  return null;
};

/** Character offsets of `range` within `el` (textContent space). */
const rangeToOffsets = (el, range) => {
  const pre = document.createRange();
  pre.selectNodeContents(el);
  pre.setEnd(range.startContainer, range.startOffset);
  const start = pre.toString().length;
  return { start, end: start + range.toString().length };
};

const findTextPoint = (el, offset) => {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let node = walker.nextNode();
  let last = null;
  while (node) {
    last = node;
    if (remaining <= node.textContent.length) {
      return { node, offset: remaining };
    }
    remaining -= node.textContent.length;
    node = walker.nextNode();
  }
  if (last) return { node: last, offset: last.textContent.length };
  return null;
};

const placeSelectionAtOffsets = (el, start, end) => {
  const a = findTextPoint(el, start);
  const b = findTextPoint(el, Math.max(start, end));
  if (!a || !b) return false;
  const range = document.createRange();
  range.setStart(a.node, a.offset);
  range.setEnd(b.node, b.offset);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

/** Re-apply selection after react-contenteditable swaps innerHTML. */
const restoreSelectionAfterRender = (el, start, end) => {
  const restore = () => {
    if (!el.isConnected) return;
    el.focus({ preventScroll: true });
    placeSelectionAtOffsets(el, start, end);
  };
  // Immediate (pre-React), then after paint, then once more like focusUtils —
  // ContentEditable may rewrite innerHTML a tick after onChange.
  restore();
  requestAnimationFrame(restore);
  setTimeout(restore, 0);
  setTimeout(restore, 45);
};

/** Empty `<code></code>` chips (ZWSP / br / nbsp only) left after deleting their text. */
export const EMPTY_INLINE_CODE_RE =
  /<code\b[^>]*>(?:\s|&nbsp;|\u00a0|\u200B|<br\s*\/?>)*<\/code>/gi;

export const stripEmptyInlineCode = (html) =>
  String(html ?? '').replace(EMPTY_INLINE_CODE_RE, '');

const isEmptyCodeEl = (node) => {
  if (node?.nodeType !== Node.ELEMENT_NODE || node.tagName !== 'CODE') return false;
  return !(node.textContent || '').replace(/[\u200B\u00a0\s]/g, '');
};

const closestEmptyCode = (node) => {
  let el = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  while (el) {
    if (isEmptyCodeEl(el)) return el;
    if (el.classList?.contains('doc-editable')) return null;
    el = el.parentElement;
  }
  return null;
};

const removeEmptyCodeAndPlaceCaret = (code) => {
  const parent = code.parentNode;
  if (!parent) return;
  const next = code.nextSibling;
  const prev = code.previousSibling;
  code.remove();

  const sel = window.getSelection();
  const range = document.createRange();
  if (prev?.nodeType === Node.TEXT_NODE) {
    range.setStart(prev, prev.textContent.length);
  } else if (next?.nodeType === Node.TEXT_NODE) {
    range.setStart(next, 0);
  } else if (prev) {
    range.setStartAfter(prev);
  } else if (next) {
    range.setStartBefore(next);
  } else {
    range.selectNodeContents(parent);
    range.collapse(true);
  }
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

/**
 * Backspace / Delete an empty inline `<code>` chip. Native contenteditable
 * cannot delete an empty element, so the caret sits on it forever.
 */
export const tryDeleteEmptyInlineCode = (e) => {
  if (e.key !== 'Backspace' && e.key !== 'Delete') return false;
  if (e.ctrlKey || e.metaKey || e.altKey) return false;

  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return false;

  const { startContainer, startOffset } = sel.getRangeAt(0);
  let code = closestEmptyCode(startContainer);

  if (!code && e.key === 'Backspace') {
    if (startContainer.nodeType === Node.TEXT_NODE) {
      if (startOffset === 0 && isEmptyCodeEl(startContainer.previousSibling)) {
        code = startContainer.previousSibling;
      }
    } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
      const prev = startContainer.childNodes[startOffset - 1];
      if (isEmptyCodeEl(prev)) code = prev;
    }
  } else if (!code && e.key === 'Delete') {
    if (startContainer.nodeType === Node.TEXT_NODE) {
      const text = startContainer.textContent || '';
      if (startOffset === text.length && isEmptyCodeEl(startContainer.nextSibling)) {
        code = startContainer.nextSibling;
      }
    } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
      const next = startContainer.childNodes[startOffset];
      if (isEmptyCodeEl(next)) code = next;
    }
  }

  if (!code) return false;
  e.preventDefault();
  removeEmptyCodeAndPlaceCaret(code);
  return true;
};

export const toggleInlineCode = () => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return false;

  const range = selection.getRangeAt(0);
  const editable = (range.startContainer.nodeType === Node.ELEMENT_NODE
    ? range.startContainer
    : range.startContainer.parentElement
  )?.closest?.('.doc-editable');
  if (!editable) return false;

  const { start, end } = rangeToOffsets(editable, range);

  const codeEl = closestCode(range.startContainer);
  if (codeEl && editable.contains(codeEl)) {
    // Unwrap the whole code element the selection sits in
    const parent = codeEl.parentNode;
    while (codeEl.firstChild) parent.insertBefore(codeEl.firstChild, codeEl);
    parent.removeChild(codeEl);
  } else {
    const code = document.createElement('code');
    try {
      range.surroundContents(code);
    } catch {
      // Selection spans element boundaries — extract and rewrap
      code.appendChild(range.extractContents());
      range.insertNode(code);
    }
  }

  editable.dispatchEvent(new Event('input', { bubbles: true }));
  restoreSelectionAfterRender(editable, start, end);
  return true;
};
